import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useScrollLock } from '../../../hooks/useScrollLock';

const CopyHotspotModal = ({
  isOpen,
  onClose,
  onConfirm,
  hotspot,
  pages,
  currentPage,
  isLoading = false
}) => {
  const [selectedPage, setSelectedPage] = useState('');
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  useScrollLock(isOpen);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedPage('');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Focus trap
    const focusableElements = modalRef?.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );
    
    if (focusableElements?.length > 0) {
      firstFocusableRef.current = focusableElements?.[0];
      lastFocusableRef.current = focusableElements?.[focusableElements?.length - 1];
      firstFocusableRef?.current?.focus();
    }

    // Keyboard support
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }

      if (e?.key === 'Tab') {
        if (e?.shiftKey) {
          if (document.activeElement === firstFocusableRef?.current) {
            e?.preventDefault();
            lastFocusableRef?.current?.focus();
          }
        } else {
          if (document.activeElement === lastFocusableRef?.current) {
            e?.preventDefault();
            firstFocusableRef?.current?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);


  if (!isOpen) return null;

  // Generate page options (exclude current page)
  const pageOptions = pages
    ?.filter(page => page?.pageNumber !== currentPage)
    ?.map(page => ({
      value: page?.pageNumber?.toString(),
      label: `Page ${page?.pageNumber}`
    })) || [];

  const handleConfirm = () => {
    // Validation
    if (!selectedPage) {
      setError('Please select a destination page');
      return;
    }

    const pageNum = parseInt(selectedPage, 10);
    if (pageNum === currentPage) {
      setError('Destination page cannot be the same as the current page');
      return;
    }

    setError(null);
    onConfirm([pageNum]); // Pass as array to match handleCopyHotspot expectation
  };

  const getHotspotIcon = (type) => {
    switch (type) {
      case 'buy':
        return 'ShoppingCart';
      case 'link':
        return 'Link';
      case 'info':
        return 'Info';
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      default:
        return 'MapPin';
    }
  };

  const getHotspotLabel = (hotspot) => {
    if (hotspot?.title) return hotspot?.title;
    if (hotspot?.url) return 'External Link';
    if (hotspot?.sku) return `SKU: ${hotspot?.sku}`;
    return `${hotspot?.type?.charAt(0)?.toUpperCase() + hotspot?.type?.slice(1)} Hotspot`;
  };

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-lg shadow-elevation-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Copy" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="modal-title" className="text-lg font-semibold text-foreground mb-1">
              Copy Hotspot to Another Page
            </h3>
            <p id="modal-description" className="text-sm text-muted-foreground">
              Select the destination page for copying this hotspot
            </p>
          </div>
        </div>

        {/* Hotspot Info */}
        {hotspot && (
          <div className="mb-4 p-3 bg-muted/50 rounded-md border border-border">
            <div className="flex items-center gap-2 mb-1">
              <Icon name={getHotspotIcon(hotspot?.type)} size={14} className="text-foreground" />
              <span className="text-sm font-medium text-foreground">
                {getHotspotLabel(hotspot)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Current page: {currentPage}
            </div>
          </div>
        )}

        {/* Page Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Destination Page
          </label>
          <Select
            value={selectedPage}
            onChange={(e) => {
              setSelectedPage(e);
              setError(null);
            }}
            options={[
              { value: '', label: 'Select a page...' },
              ...pageOptions
            ]}
            disabled={isLoading}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-3 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading || !selectedPage}
            iconName="Copy"
            iconPosition="left"
          >
            Copy Hotspot
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CopyHotspotModal;